import type {
  PortableTextBlockComponent,
  PortableTextComponentProps,
  PortableTextComponents,
} from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";

import { createElement } from "react";
import { AccentHeading } from "./AccentText";

const HEADING_BLOCK_STYLES = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

/**
 * Marks cannot see the parent block style. Custom heading renderers replace
 * the default heading, so the accent context is applied to every h1–h6 here.
 * Otherwise an override such as feature-hero would keep outline fill solid.
 */
function wrapHeading(heading: PortableTextBlockComponent) {
  return function HeadingWithAccent(
    props: PortableTextComponentProps<PortableTextBlock>,
  ) {
    return createElement(AccentHeading, null, createElement(heading, props));
  };
}

function wrapHeadingBlocks(
  block: PortableTextComponents["block"],
): PortableTextComponents["block"] {
  if (!block || typeof block === "function") {
    return block;
  }

  const wrapped = { ...block };

  for (const style of HEADING_BLOCK_STYLES) {
    const heading = block[style];

    if (!heading) {
      continue;
    }

    wrapped[style] = wrapHeading(heading);
  }

  return wrapped;
}

function mergePortableTextComponents(
  defaults: Partial<PortableTextComponents>,
  overrides?: Partial<PortableTextComponents>,
): PortableTextComponents {
  return {
    ...defaults,
    ...overrides,
    block: wrapHeadingBlocks({
      ...defaults.block,
      ...overrides?.block,
    } as PortableTextComponents["block"]),
    list: {
      ...defaults.list,
      ...overrides?.list,
    } as PortableTextComponents["list"],
    listItem: {
      ...defaults.listItem,
      ...overrides?.listItem,
    } as PortableTextComponents["listItem"],
    marks: {
      ...defaults.marks,
      ...overrides?.marks,
    },
    types: {
      ...defaults.types,
      ...overrides?.types,
    },
  };
}

export { mergePortableTextComponents };
